import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/axios';
import { auth } from '../config/firebase';

const OrganizationContext = createContext();

// Debug log for API URL
console.log('API URL:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV
});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    console.warn('useOrganization must be used within OrganizationProvider');
    return { 
      organizationData: null,
      organizationId: null,
      personData: null,
      loading: false, 
      error: null,
      refetch: () => {} 
    };
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [organizationData, setOrganizationData] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [personData, setPersonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      console.log('Attempting to fetch user organization data:', {
        uid: currentUser.uid,
        endpoint: `/api/dashboard/user/${currentUser.uid}`
      });

      const userResponse = await api.get(`/api/dashboard/user/${currentUser.uid}`).catch(err => {
        console.error('User data fetch failed:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message
        });
        throw err;
      });

      const { organizationId: orgId, personData: userData } = userResponse.data;
      
      if (!orgId) {
        throw new Error('No organization found for user');
      }

      console.log('Successfully fetched user data:', {
        orgId,
        hasPersonData: !!userData
      });

      setOrganizationId(orgId);
      setPersonData(userData);

      console.log('Fetching company details:', {
        endpoint: `/api/dashboard/company/${orgId}`
      });

      const response = await api.get(`/api/dashboard/company/${orgId}`).catch(err => {
        console.error('Company data fetch failed:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message
        });
        throw err;
      });

      setOrganizationData(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error in fetchOrganizationData:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
      setError(err.message || 'Failed to fetch organization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  return (
    <OrganizationContext.Provider 
      value={{ 
        organizationData,
        organizationId,
        personData,
        loading, 
        error,
        refetch: fetchOrganizationData 
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}; 